import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ButtonGroup, Button } from 'reactstrap';
import LaunchIcon from '@material-ui/icons/Launch';
import { getNotifications } from '../../store/general/selectors';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import { NotificationTypeEnum } from '../../models';
import { openEntity, openAppointment, openClient } from '../../store/entities/actions';
import { getPackAsync } from '../../ajax/requests';
import { formatEuros, shortenName } from '../../shared/string';
import { useGeneralSettings } from '../../store/settings/hooks';
import StyledTable from '../../components/StyledTable';
import { TotalsGrid, StatCard, CardLabel, CardValue, CardSubValue } from './BillingStatCards';
import { BillingResponsiveBar } from './BillingResponsiveBar';
import { AutocompleteTextField } from '../../components/Forms/fields';
import { DateRangeSelector, DateRangeSelectorState } from '../../components/DateRangeSelector';
import { buildDefaultDebtDateRange, buildDebtRows } from './debtTabHelpers';
import styled from 'styled-components';
import './Billing.scss';

type FilterType = 'all' | 'appointment' | 'pack';
type SortField = 'date' | 'value' | 'client';
type SortDir = 'asc' | 'desc';

const ClientLink = styled.span<{ onClick?: (e: React.MouseEvent) => void; children?: React.ReactNode }>`
    cursor: pointer;
    &:hover {
        text-decoration: underline;
    }
`;

const ServiceCell = styled.td<{ title?: string; children?: React.ReactNode }>`
    max-width: 180px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const ActionButton = styled(Button)`
    display: inline-flex;
    align-items: center;
    gap: var(--spacer-XXS);
    padding: 0;
    font-size: 0.85rem;
    white-space: nowrap;
`;

const SortableTh = styled.th<{ active?: boolean; children?: React.ReactNode; onClick?: () => void }>`
    cursor: pointer;
    user-select: none;
    white-space: nowrap;
    color: ${({ active }) => active ? 'var(--appBlue)' : 'inherit'};

    &:hover {
        color: var(--appBlue);
    }
`;

const EmptyState = styled.div`
    padding: var(--spacer-L);
    text-align: center;
    color: var(--text-muted);
`;

const ChartsGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--spacer-M);
    margin-bottom: var(--spacer-M);

    @media (max-width: 900px) {
        grid-template-columns: 1fr;
    }
`;

const ChartSection = styled.div`
    background: var(--bg-light);
    border-radius: 6px;
    padding: var(--spacer-M);
`;

const ChartTitle = styled.div`
    font-size: 0.8rem;
    color: var(--text-muted);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-weight: 600;
    margin-bottom: var(--spacer-S);
`;

export const DebtTab = () => {
    const t = useTranslations();
    const dispatch = useDispatch();
    const notifications = useSelector(getNotifications);
    const generalSettings = useGeneralSettings();
    const defaultDateRange = React.useMemo(() => buildDefaultDebtDateRange(), []);

    const [filterType, setFilterType] = useState<FilterType>('all');
    const [dateRange, setDateRange] = useState<DateRangeSelectorState>(defaultDateRange);
    const [clientFilters, setClientFilters] = useState<string[]>([]);
    const [serviceFilters, setServiceFilters] = useState<string[]>([]);
    const [sortField, setSortField] = useState<SortField>('date');
    const [sortDir, setSortDir] = useState<SortDir>('desc');

    const missingPaymentRows = useMemo(
        () => buildDebtRows(
            notifications.filter(n => [
                NotificationTypeEnum.AppointmentMissingPayment,
                NotificationTypeEnum.PackMissingPayment,
            ].includes(n.type)),
            generalSettings
        ),
        [notifications, generalSettings]
    );

    const allClients = useMemo(
        () => [...new Set(missingPaymentRows.map(({ notification }) => notification.clientName).filter((c): c is string => !!c))].sort(),
        [missingPaymentRows]
    );

    const allServiceTypes = useMemo(
        () => [...new Set(missingPaymentRows.map(({ serviceLabel }) => serviceLabel).filter((s): s is string => !!s))].sort(),
        [missingPaymentRows]
    );

    const filtered = useMemo(() => {
        let result = missingPaymentRows;

        if (filterType === 'appointment') {
            result = result.filter(({ notification }) => notification.type === NotificationTypeEnum.AppointmentMissingPayment);
        } else if (filterType === 'pack') {
            result = result.filter(({ notification }) => notification.type === NotificationTypeEnum.PackMissingPayment);
        }

        if (dateRange) {
            result = result.filter(({ notification }) => {
                const d = notification.date.substring(0, 10);
                return d >= dateRange.from.substring(0, 10) && d <= dateRange.to.substring(0, 10);
            });
        }

        if (clientFilters.length > 0) {
            result = result.filter(({ notification }) => notification.clientName && clientFilters.includes(notification.clientName));
        }

        if (serviceFilters.length > 0) {
            result = result.filter(({ serviceLabel }) => serviceFilters.includes(serviceLabel));
        }

        return [...result].sort((a, b) => {
            let cmp = 0;
            if (sortField === 'date') {
                cmp = (a.notification.date ?? '').localeCompare(b.notification.date ?? '');
            } else if (sortField === 'value') {
                cmp = a.amount - b.amount;
            } else {
                cmp = (a.notification.clientName ?? '').localeCompare(b.notification.clientName ?? '');
            }
            return sortDir === 'asc' ? cmp : -cmp;
        });
    }, [missingPaymentRows, filterType, dateRange, clientFilters, serviceFilters, sortField, sortDir]);

    const total = useMemo(
        () => filtered.reduce((sum, { amount }) => sum + amount, 0),
        [filtered]
    );

    const appointmentCount = useMemo(
        () => filtered.filter(({ notification }) => notification.type === NotificationTypeEnum.AppointmentMissingPayment).length,
        [filtered]
    );

    const packCount = useMemo(
        () => filtered.filter(({ notification }) => notification.type === NotificationTypeEnum.PackMissingPayment).length,
        [filtered]
    );

    const appointmentKey = t(TK.appointment);
    const packKey = t(TK.pack);
    const totalKey = t(TK.total);

    const debtByMonthData = useMemo(() => {
        const byMonth: Record<string, Record<string, number>> = {};
        for (const { notification, amount } of filtered) {
            const month = notification.date.substring(0, 7);
            if (!byMonth[month]) byMonth[month] = { [appointmentKey]: 0, [packKey]: 0 };
            if (notification.type === NotificationTypeEnum.AppointmentMissingPayment) {
                byMonth[month][appointmentKey] += amount;
            } else {
                byMonth[month][packKey] += amount;
            }
        }
        return Object.entries(byMonth)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([label, vals]) => ({ label, ...vals }));
    }, [filtered, appointmentKey, packKey]);

    const topDebtorsData = useMemo(() => {
        const byClient: Record<string, number> = {};
        filtered.forEach(({ notification, amount }) => {
            const name = notification.clientName ?? '?';
            byClient[name] = (byClient[name] ?? 0) + amount;
        });
        return Object.entries(byClient)
            .sort(([, a], [, b]) => a - b)
            .slice(-10)
            .map(([label, value]) => ({ label, [totalKey]: value }));
    }, [filtered, totalKey]);

    const handleSort = useCallback((field: SortField) => {
        if (sortField === field) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDir('desc');
        }
    }, [sortField]);

    const sortIcon = (field: SortField) => sortField === field ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ' ↕';

    const onOpenEntity = useCallback((entityId: string, type: NotificationTypeEnum) => (e: React.MouseEvent) => {
        e.stopPropagation();
        if (type === NotificationTypeEnum.AppointmentMissingPayment) {
            dispatch(openAppointment(entityId));
        } else {
            dispatch(openEntity('clientPacks', entityId, () => getPackAsync(entityId)));
        }
    }, [dispatch]);

    const onOpenClient = useCallback((clientId: string) => (e: React.MouseEvent) => {
        e.stopPropagation();
        dispatch(openClient(clientId));
    }, [dispatch]);

    const typeLabel = (type: NotificationTypeEnum) =>
        type === NotificationTypeEnum.PackMissingPayment
            ? `${t(TK.pack)}/${t(TK.subscription)}`
            : t(TK.appointment);

    return (
        <div className="billingContainer">
            <DateRangeSelector
                onRangeChanged={setDateRange}
                defaultType="range"
                defaultStartDay={defaultDateRange.from}
                defaultEndDay={defaultDateRange.to}
            />

            <div className="billTypePanel">
                <ButtonGroup>
                    <Button outline color="info" active={filterType === 'all'} onClick={() => setFilterType('all')}>
                        {t(TK.all)}
                    </Button>
                    <Button outline color="info" active={filterType === 'appointment'} onClick={() => setFilterType('appointment')}>
                        {t(TK.appointment)}
                    </Button>
                    <Button outline color="info" active={filterType === 'pack'} onClick={() => setFilterType('pack')}>
                        {t(TK.pack)}/{t(TK.subscription)}
                    </Button>
                </ButtonGroup>

                <AutocompleteTextField
                    options={allClients}
                    values={clientFilters}
                    onValuesChanged={setClientFilters}
                    multiSelect
                    placeholder={clientFilters.length === 0 ? `${t(TK.client)}...` : undefined}
                />

                <AutocompleteTextField
                    options={allServiceTypes}
                    values={serviceFilters}
                    onValuesChanged={setServiceFilters}
                    multiSelect
                    placeholder={serviceFilters.length === 0 ? `${t(TK.service)}/${t(TK.pack)}...` : undefined}
                />
            </div>

            <TotalsGrid style={{ marginTop: 'var(--spacer-M)' }}>
                <StatCard accent="var(--danger-color)">
                    <CardLabel>{t(TK.total)}</CardLabel>
                    <CardValue textColor="var(--danger-color)">{formatEuros(total)}</CardValue>
                    <CardSubValue>{filtered.length} {t(TK.debtors).toLowerCase()}</CardSubValue>
                </StatCard>
                {(filterType === 'all' || filterType === 'appointment') && appointmentCount > 0 && (
                    <StatCard accent="var(--appLigthGrey)" style={{ cursor: 'pointer' }} onClick={() => setFilterType('appointment')}>
                        <CardLabel>{t(TK.appointments)}</CardLabel>
                        <CardValue textColor="var(--text-body)">
                            {formatEuros(filtered
                                .filter(n => n.notification.type === NotificationTypeEnum.AppointmentMissingPayment)
                                .reduce((s, n) => s + n.amount, 0)
                            )}
                        </CardValue>
                        <CardSubValue>{appointmentCount} {t(TK.appointments).toLowerCase()}</CardSubValue>
                    </StatCard>
                )}
                {(filterType === 'all' || filterType === 'pack') && packCount > 0 && (
                    <StatCard accent="var(--appLigthGrey)" style={{ cursor: 'pointer' }} onClick={() => setFilterType('pack')}>
                        <CardLabel>{t(TK.packs)}</CardLabel>
                        <CardValue textColor="var(--text-body)">
                            {formatEuros(filtered
                                .filter(n => n.notification.type === NotificationTypeEnum.PackMissingPayment)
                                .reduce((s, n) => s + n.amount, 0)
                            )}
                        </CardValue>
                        <CardSubValue>{packCount} {t(TK.packs).toLowerCase()}</CardSubValue>
                    </StatCard>
                )}
            </TotalsGrid>

            {filtered.length > 0 && (
                <ChartsGrid>
                    <ChartSection>
                        <ChartTitle>{t(TK.debtByMonth)}</ChartTitle>
                        <BillingResponsiveBar
                            data={debtByMonthData}
                            keys={[appointmentKey, packKey]}
                            groupMode="stacked"
                        />
                    </ChartSection>
                    <ChartSection>
                        <ChartTitle>{t(TK.topDebtors)}</ChartTitle>
                        <BillingResponsiveBar
                            data={topDebtorsData}
                            keys={[totalKey]}
                            sortByLabel={false}
                        />
                    </ChartSection>
                </ChartsGrid>
            )}

            {filtered.length === 0 ? (
                <EmptyState>{t(TK.noData)}</EmptyState>
            ) : (
                <StyledTable scrollable hover>
                    <thead>
                        <tr>
                            <SortableTh active={sortField === 'date'} onClick={() => handleSort('date')}>
                                {t(TK.date)}{sortIcon('date')}
                            </SortableTh>
                            <th>{t(TK.type)}</th>
                            <SortableTh active={sortField === 'client'} onClick={() => handleSort('client')}>
                                {t(TK.client)}{sortIcon('client')}
                            </SortableTh>
                            <th>{t(TK.service)}</th>
                            <SortableTh active={sortField === 'value'} onClick={() => handleSort('value')}>
                                {t(TK.value)}{sortIcon('value')}
                            </SortableTh>
                            <th/>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map(({ notification, amount, serviceLabel }, i) => (
                            <tr key={notification.entityId ?? `${notification.date}-${i}`}>
                                <td style={{ whiteSpace: 'nowrap' }}>{notification.date.substring(0, 10)}</td>
                                <td>{typeLabel(notification.type)}</td>
                                <td>
                                    {notification.clientId ? (
                                        <ClientLink onClick={onOpenClient(notification.clientId)}>
                                            {shortenName(notification.clientName ?? '')}
                                        </ClientLink>
                                    ) : (
                                        shortenName(notification.clientName ?? '')
                                    )}
                                </td>
                                <ServiceCell title={serviceLabel}>{serviceLabel}</ServiceCell>
                                <td style={{ color: 'var(--danger-color)', fontWeight: 600 }}>{formatEuros(amount)}</td>
                                <td>
                                    <ActionButton color="link" onClick={onOpenEntity(notification.entityId!, notification.type)}>
                                        <LaunchIcon style={{ fontSize: '1rem' }} />
                                        {notification.type === NotificationTypeEnum.PackMissingPayment
                                            ? `${t(TK.view)} ${t(TK.pack).toLowerCase()}`
                                            : `${t(TK.view)} ${t(TK.appointment).toLowerCase()}`}
                                    </ActionButton>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </StyledTable>
            )}
        </div>
    );
};
